// External dependencies

import { Capacitor } from '@capacitor/core';
import { type ReactNode, Suspense, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import GuestWall from '../components/auth/GuestWall';
import VerifyEmailGate from '../components/auth/VerifyEmailGate';
import CircleStartBanner from '../components/circles/CircleStartBanner';
import PendingCircleJoin from '../components/circles/PendingCircleJoin';
import PublicStartingSoonHost from '../components/circles/PublicStartingSoonHost';
import ListenerRingHost from '../components/ring/ListenerRingHost';
// Router bootstrap hooks (extracted from inline useEffects per T2)
import {
  useAppServices,
  useAuthInit,
  useNativeBackButton,
  useNetworkStatus,
  useOrientationSafeArea,
} from '../hooks/useAppBootstrap';
import Course from '../pages/dashboard/course';
import ForgotPassword from '../pages/forgot-password';
import GoogleCallback from '../pages/google-callback';
// Lazy-loaded page components (code splitting for better initial load)
import LandingPage from '../pages/landing';
// Core page components (loaded immediately for fast initial render)
import Login from '../pages/login';
import NotFound from '../pages/not-found';
import PageLoader from '../pages/page-loader';
import Privacy from '../pages/privacy';
import Register from '../pages/register';
import ResetPassword from '../pages/reset-password';
import TermsOfUse from '../pages/terms-of-use';
import { initPushNotifications, setNotificationNavigator } from '../services/push.service';
import { useWebPushResync } from '../hooks/useWebPushResync';
// Store imports
import { useAuthStore } from '../stores/auth.store';
import {
  notificationLoginRedirect,
  signInDestination,
  useNotificationRouteStore,
} from '../stores/notification-route.store';
import { DEBUG_SURFACES_ENABLED } from '../utils/debugSurfaces';
import { isGuestAllowedPath } from '../utils/guestRoutes';
import { lazyWithReload } from '../utils/lazyWithReload';
import ChunkErrorBoundary from './ChunkErrorBoundary';
import PublicRoute from './PublicRoute';

const Progress = lazyWithReload(() => import('../pages/dashboard/progress'));
const Profile = lazyWithReload(() => import('../pages/dashboard/profile'));
const CreditsUsagePage = lazyWithReload(() => import('../pages/dashboard/CreditsUsagePage'));
const ReferralsPage = lazyWithReload(() => import('../pages/dashboard/referrals'));
const LanguagesPage = lazyWithReload(() => import('../pages/dashboard/languages'));
const PublicLanguagePage = lazyWithReload(() => import('../pages/languages/public'));
const NotificationPreferencesPage = lazyWithReload(
  () => import('../pages/dashboard/NotificationPreferencesPage'),
);
const PrivacyDashboard = lazyWithReload(() => import('../pages/dashboard/privacy-settings'));
const Tools = lazyWithReload(() => import('../pages/dashboard/tools'));
const Empathy = lazyWithReload(() => import('../pages/dashboard/empathy'));
const ListeningSchedule = lazyWithReload(() => import('../pages/dashboard/listening-schedule'));
const BookedCall = lazyWithReload(() => import('../pages/bookings/booked-call'));
const MyBookings = lazyWithReload(() => import('../pages/bookings/my-bookings'));
const RescheduleBooking = lazyWithReload(() => import('../pages/bookings/reschedule'));
const BookACall = lazyWithReload(() => import('../pages/bookings/book-a-call'));
const BookByTime = lazyWithReload(() => import('../pages/bookings/book-by-time'));
const BookByPerson = lazyWithReload(() => import('../pages/bookings/book-by-person'));
const Conversations = lazyWithReload(() => import('../pages/dashboard/conversations'));
const Wallet = lazyWithReload(() => import('../pages/dashboard/wallet'));
const Lesson = lazyWithReload(() => import('../pages/lesson'));
const Quiz = lazyWithReload(() => import('../pages/quiz'));
const AiTutor = lazyWithReload(() => import('../pages/ai-tutor'));
const LessonResults = lazyWithReload(() => import('../pages/lesson-results'));
const Games = lazyWithReload(() => import('../pages/dashboard/games'));
const GiraffeTranslator = lazyWithReload(() => import('../pages/tools/giraffe-translator'));
const NeedsGuesser = lazyWithReload(() => import('../pages/tools/needs-guesser'));
const TestPathCompletion = lazyWithReload(() => import('../pages/test-path-completion'));
const TestCourseCompletion = lazyWithReload(() => import('../pages/test-course-completion'));
const AchievementTest = lazyWithReload(() => import('../pages/dev/achievement-test'));
const TippingTest = lazyWithReload(() => import('../pages/dev/tipping-test'));
const PublicProfile = lazyWithReload(() => import('../pages/profile/public-profile'));
const SubscriptionSuccess = lazyWithReload(() => import('../pages/subscription/success'));
const SubscriptionCancel = lazyWithReload(() => import('../pages/subscription/cancel'));
const EmailVerify = lazyWithReload(() => import('../pages/email-verify'));
const RsvpLinkPage = lazyWithReload(() => import('../pages/rsvp-link'));
const EmailVerifyResult = lazyWithReload(() => import('../pages/email-verify-result'));
const QuizAssessment = lazyWithReload(() => import('../pages/quiz-assessment'));
const Waitlist = lazyWithReload(() => import('../pages/waitlist'));
const ShareResults = lazyWithReload(() => import('../pages/quiz-assessment/ShareResults'));
const LearnIndex = lazyWithReload(() => import('../pages/learn/index'));
const LearnArticle = lazyWithReload(() => import('../pages/learn/article'));
const ConflictResolution = lazyWithReload(() => import('../pages/conflict-resolution/index'));
const CircleSessionPage = lazyWithReload(() => import('../pages/circles/session'));
/** /dev/turn-display: the turn display with three mock people. Gated like the ones below. */
const TurnDisplayPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/turn-display-preview'))
  : null;
/**
 * GATED AT THE IMPORT, not only at the route (independent review, F7).
 *
 * The route below is `import.meta.env.DEV`-gated, but a bare `lazy(() =>
 * import(...))` here still hands Rollup a reachable dynamic import, so the
 * whole preview — six fake participants, the canvas camera, the giraffe
 * avatars — shipped as a chunk in every production and Capacitor build to be
 * fetched by nobody. Vite statically replaces `import.meta.env.DEV` with
 * `false` in a production build, so the ternary folds and the `import()`
 * becomes dead code Rollup drops. Verified by grepping `dist/` for a chunk
 * named after it after `npm run build`.
 */
const AmbientViewPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/ambient-view-preview'))
  : null;
/**
 * /dev/circle-preview: every circle screen with fake participants and a
 * stand-in Janus. Gated like the one above (David's review round 1, q23): it
 * was left reachable for on-device debugging of a Capacitor build, and so it
 * shipped six fake people in every production build. On-device debugging
 * keeps the audio and circle test pages below.
 */
const CirclePreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/circle-preview'))
  : null;
/** /dev/session-menu — the in-circle "⋯" and its sheet. Gated like the one above. */
const SessionMenuPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/session-menu-preview'))
  : null;
/** /dev/camera-coach — the camera coach mark over the real control row. Gated like the one above. */
const CameraCoachPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/camera-coach-preview'))
  : null;
/** /dev/design-system: the redesign primitives, scenes, sprites and tokens. Gated like the one above. */
const DesignSystemGallery = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/design-system'))
  : null;
/** /dev/tools-preview: the tool-page states no route renders (the translator's modal, both upgrade gates). Gated like the one above. */
const ToolsPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/tools-preview'))
  : null;
/** /dev/toasts: every toast type through the real container (the before/after screenshots). Gated like the one above. */
const ToastsPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/toasts-preview'))
  : null;
const ReactionTest = lazyWithReload(() => import('../pages/dev/reaction-test'));
const AudioTest = lazyWithReload(() => import('../pages/dev/audio-test'));
const JanusAudioTest = lazyWithReload(() => import('../pages/dev/janus-audio-test'));
const CircleTestRoom = lazyWithReload(() => import('../pages/dev/circle-test-room'));
const CircleRehearsal = lazyWithReload(() => import('../pages/dev/circle-rehearsal'));
/**
 * Gated at the import AND at the route, the AmbientViewPreview way above — but
 * on the flag that gates the only in-app door to it, not on DEV alone.
 *
 * The only in-app door to this page is a button in `SubscriptionDebug`, which
 * `profile.tsx` mounts behind `VITE_SUBSCRIPTION_DEBUG === 'true'`
 * (profile.tsx:1696). Reusing that flag registers the route in exactly the
 * builds that carry its entry point and drops it — chunk included — from the
 * ones that do not, verified by grepping `dist/` after a build in each mode.
 *
 * DEV alone would have been STRICTER THAN THE PANEL and would have broken the
 * page on staging, where the backend routes ARE registered
 * (`local|testing|staging` in routes/api.php) and the admin card renders a
 * `/dev/empathy-test-call?session=N` deep link to paste into the SPA — a link
 * that would then dead-end.
 *
 * WHERE THE FLAG COMES FROM, and why it fails safe, is written out once in
 * `utils/debugSurfaces.ts` — which is now the only spelling of it, because the
 * cap box beside the button on THIS page was gated on `DEV` alone and was
 * therefore missing from the one build a phone runs.
 */
const EmpathyTestCall = DEBUG_SURFACES_ENABLED
  ? lazyWithReload(() => import('../pages/dev/empathy-test-call'))
  : null;
/*
 * Design galleries and previews with fake people or seeded data (#1058
 * follow-up): gated at the import and at the route like AmbientViewPreview,
 * so no production or Capacitor build carries or fetches them. Every /dev
 * route is classified in app/__tests__/dev-routes-gated.test.ts.
 */
const SuperReactionsGallery = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/super-reactions-gallery'))
  : null;
const FavoriteExplainerGallery = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/favorite-explainer-gallery'))
  : null;
const RedditDiscussPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/reddit-discuss-preview'))
  : null;
const CohortSettingsPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/cohort-settings-preview'))
  : null;
const OrganizerToolsPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/organizer-tools-preview'))
  : null;
const HistoryCardPreview = import.meta.env.DEV
  ? lazyWithReload(() => import('../pages/dev/history-card-preview'))
  : null;
const SoundPanel = lazyWithReload(() => import('../pages/dev/sound-panel'));
const QuizRefPage = lazyWithReload(() => import('../pages/quiz-ref'));
const AiBuddySessionPage = lazyWithReload(() => import('../pages/ai-buddy/session'));
const CohortDetailPage = lazyWithReload(() => import('../pages/circles/cohort-detail'));
const CreateCohortPage = lazyWithReload(() => import('../pages/circles/create-cohort'));
const CohortDashboardPage = lazyWithReload(() => import('../pages/circles/cohort-dashboard'));
const JoinInvitePage = lazyWithReload(() => import('../pages/circles/join-invite'));
const PublicCohortPage = lazyWithReload(() => import('../pages/circles/public-cohort'));

// Celebration components
import CelebrationModal from '../components/celebrations/CelebrationModal';
import { useCelebrationStore } from '../stores/celebration.store';
import type { CourseCelebrationData } from '../types';

// Dev-mode: quick console helpers for testing celebrations.
// These attach demo functions to `window` so a developer can call them from
// the browser console — declare the slots on the Window type rather than
// casting through `any`.
declare global {
  interface Window {
    demoDomain?: () => void;
    demoCourse?: (overrides?: Partial<CourseCelebrationData>) => void;
    demoSound?: (name?: string) => string;
  }
}
if (import.meta.env.DEV) {
  const w = window;
  const seed = () => {
    localStorage.setItem(
      'journey_memory',
      JSON.stringify({
        selectedGoal: 'communicate-better',
        goalLabel: 'Communicate better with my partner',
        toolUsed: 'giraffe-translator',
        toolInputText: 'You never listen to me when I try to tell you how I feel!',
        savedAt: new Date().toISOString(),
      }),
    );
    useCelebrationStore.getState().resetShownCelebrations();
    localStorage.removeItem('empathy_tour_complete');
  };
  w.demoDomain = () => {
    seed();
    useCelebrationStore.getState().showDomainCelebration({
      domainId: 1,
      domainName: 'Introduction to NVC',
      pathsCompleted: 3,
      lessonsCompleted: 12,
      averageScore: 87,
      pointsEarned: 340,
      isPremium: false,
    });
    useCelebrationStore.getState().showEmpathyUnlockCelebration();
  };
  // demoCourse({ ... }) overrides any field (the celebrations shots spec
  // passes the eight-module course).
  w.demoCourse = (overrides) => {
    seed();
    useCelebrationStore.getState().showCourseCelebration({
      lessonsCompleted: 48,
      totalLessons: 48,
      totalPoints: 2400,
      daysToComplete: 21,
      pathScores: [92, 88, 95, 90],
      averageScore: 91,
      isPremium: true,
      ...overrides,
    });
  };
  // Play celebration sounds without autoplay restrictions (click the page first, then run)
  w.demoSound = (name?: string) => {
    const sounds: Record<string, string> = {
      domain: '/path_complete.opus',
      course: '/course_complete.opus',
      choir: '/choir.opus',
    };
    const src = sounds[name || 'choir'] || sounds.choir;
    const audio = new Audio(src);
    audio
      .play()
      .catch(() => console.log('Click anywhere on the page first, then re-run demoSound()'));
    return `Playing ${src}`;
  };
}

import OnboardingNavGuard from '../components/onboarding/OnboardingNavGuard';

// Onboarding
import OnboardingOverlay from '../components/onboarding/OnboardingOverlay';
// Trial modals
import TrialModals from '../components/trial/TrialModals';
import { usePendingLanguageRequest } from '../hooks/usePendingLanguageRequest';
import { loginUrl } from '../utils/safe-redirect';

/**
 * Lazy loading fallback component
 */
function LazyLoadFallback() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="loading loading-spinner loading-lg text-primary"></div>
    </div>
  );
}

/**
 * Where the lazy pages render: the Suspense loader, and above it the
 * chunk-load boundary (backlog c-028), so a page whose chunk is gone after a
 * deploy shows a reload prompt (app/ChunkErrorBoundary) instead of blanking
 * the app. Keyed on the pathname so the prompt clears on back/forward; its own
 * component so AppRouter does not re-render on every navigation.
 */
function LazyRoutes({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ChunkErrorBoundary resetKey={pathname}>
      <Suspense fallback={<LazyLoadFallback />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

// Debug components
// import DebugInfo from "../components/debug-info";

/**
 * Protected Route wrapper component
 * Redirects to login if user is not authenticated
 */
export function ProtectedRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isGuest = useAuthStore((state) => state.isGuest);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const isResolving = useNotificationRouteStore((state) => state.isResolving);
  const pushedRoute = useNotificationRouteStore(notificationLoginRedirect);
  const location = useLocation();
  // The PendingLanguageRequest runner runs BEFORE the verification wall below,
  // so a brand-new unverified account records the language it came for from a
  // share link right after signup (LANGUAGE-REQUESTS.md L4, §9).
  usePendingLanguageRequest(isAuthenticated && !isGuest && user !== null && user !== undefined);
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated && !isGuest) {
    // Preserve where the user was headed so login can return them there after
    // sign-in (login.tsx reads ?redirect= with an open-redirect guard). Lets a
    // shared circle link survive the auth bounce instead of dumping to home.
    // A tapped push whose `/me` failed on a cold start goes where the push
    // said, not to the `/app` the cold start sits on (NOTIFICATION-TAP-ROUTES
    // decision 5); any other page is its own destination (c-080).
    return <Navigate to={loginUrl(signInDestination(pushedRoute, location))} replace />;
  }
  // Guests: default-deny. Any /app route without explicit tour support or a tailored
  // guest wall renders the generic wall — otherwise unwrapped authed pages (e.g.
  // /app/ai-buddy, live circle sessions) are deep-linkable into broken 401 flows
  // (Codex #709). New routes are guest-denied until deliberately allowlisted.
  if (isGuest && !isGuestAllowedPath(location.pathname)) {
    return (
      <GuestWall
        title="This area needs an account"
        description="Create a free account to use this part of EmpathEase — the tour covers the intro course, games, and more."
      />
    );
  }
  // A new (post-cutoff) user with an unverified email is walled here — the client
  // mirror of the server `verified-new` gate. DELIBERATELY app-wide (product call):
  // new accounts verify before using the app; the screen offers resend + change-email,
  // and the server additionally enforces the paid surfaces via `verified-new`.
  // Grandfathered users are never walled; they see a dismissible banner instead.
  // Guests (no user object) skip this — they have no email to verify.
  const needsVerification = user?.verification_required === true && user?.email_verified === false;
  if (needsVerification) {
    return <VerifyEmailGate />;
  }
  if (isResolving && Capacitor.isNativePlatform()) return <PageLoader />;
  // Note: the guest + verify-email banners render inside DashboardLayout (below its
  // fixed header) — mounted here they'd sit underneath the fixed navbar, invisible.
  return (
    <>
      <CircleStartBanner />
      {/* Behind VerifyEmailGate on purpose: an unverified account never runs a
          visitor's pending circle join (CIRCLE-STARTING-NOTICE.md §5, N12). */}
      <PendingCircleJoin />
      <ListenerRingHost />
      <Outlet />
    </>
  );
}

/**
 * Renders the guest element while touring (no account), the real page otherwise.
 * Keeps guest branching out of the authed pages themselves.
 */
function GuestGated({ children, guest }: { children: React.ReactNode; guest: React.ReactNode }) {
  const isGuest = useAuthStore((state) => state.isGuest);
  return <>{isGuest ? guest : children}</>;
}

/**
 * Dynamic Redirect component for routes with parameters
 * Properly preserves URL parameters when redirecting
 */
function DynamicRedirect({ to }: { to: string }) {
  const params = useParams();
  // Replace :param placeholders with actual values
  let targetPath = to;
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      targetPath = targetPath.replace(`:${key}`, value);
    }
  });
  return <Navigate to={targetPath} replace />;
}

/**
 * A native cold start's redirect off `/`, made only while the browser is still
 * on the page this render shows. BrowserRouter moves in a transition, so a
 * deep link's navigation (an email link, a push tap) is already in the
 * browser's history while the router still renders `/` (the target page's
 * lazy chunk is loading). A plain `<Navigate replace>` rendered from that
 * stale `/` replaced the link's entry: an email link tapped on a cold start
 * landed on /app (or /login) with nothing done (F6 review,
 * cold-start-deep-link.test.tsx). When the browser has moved on, the newer
 * navigation lands instead. Checked in the effect, at the moment of the
 * replace, not in the render.
 */
function RedirectOffLanding({ to }: { to: string }) {
  const navigate = useNavigate();
  const { pathname, key } = useLocation();
  useEffect(() => {
    if (window.location.pathname !== pathname) return;
    void navigate(to, { replace: true });
  }, [navigate, to, pathname, key]);
  return <PageLoader />;
}

/**
 * Landing Route component
 * Shows landing page for web browsers, redirects native apps to login
 */
export function LandingRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isResolving = useNotificationRouteStore((state) => state.isResolving);
  const pushedRoute = useNotificationRouteStore(notificationLoginRedirect);
  const isNative = Capacitor.isNativePlatform();

  if (isLoading) {
    return <PageLoader />;
  }

  // Native app: authenticated users go to app, others go to login
  if (isNative) {
    if (isAuthenticated) {
      return <RedirectOffLanding to="/app" />;
    }
    // A native cold start sits HERE, not on /app: a push tapped while `/me`
    // fails rides the login's `?redirect=` (decision 5), because the sign-in
    // starts a new session and drops the pending route (#940). The tap event
    // can land after `/me` fails, so wait for it, as ProtectedRoute does.
    if (isResolving) return <PageLoader />;
    return <RedirectOffLanding to={loginUrl(pushedRoute)} />;
  }

  // Web: always show landing page (navbar already adapts for auth'd users)
  return <LandingPage />;
}

/**
 * Responsible for initializing push notifications
 * once the user is authenticated and the app is
 * running on a native (Capacitor) platform.
 *
 * Responsibilities:
 * - Watch authentication state.
 * - If user is authenticated AND app is running natively:
 *      • Register navigation handler for notifications.
 *      • Initialize push notification setup.
 *
 * Dependencies:
 * - useAuthStore → Authentication state
 * - Capacitor.isNativePlatform() → Platform check
 * - setNotificationNavigator() → Stores navigation reference
 * - initPushNotifications() → Registers FCM / native push listeners
 */
function AppInit() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const pendingRoute = useNotificationRouteStore((s) => s.pendingRoute);

  useEffect(() => {
    if (isAuthenticated && Capacitor.isNativePlatform()) {
      setNotificationNavigator(navigate);
      void initPushNotifications();
    }
  }, [isAuthenticated, navigate]);

  // Web: re-send this browser's push subscription for every new session.
  useWebPushResync();

  // After auth settles, consume any pending notification deep link.
  // Uses requestAnimationFrame to ensure React Router has fully mounted
  // before navigating — without this, cold-start navigations get
  // overwritten by the initial route render.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !pendingRoute) return;
    const route = useNotificationRouteStore.getState().consumePendingRoute();
    if (route) {
      requestAnimationFrame(() => {
        void navigate(route, { replace: true });
      });
    }
  }, [isLoading, isAuthenticated, pendingRoute, navigate]);

  return null;
}

/**
 * Main Router component for the application
 * Handles authentication, offline state, and navigation
 */
function AppRouter() {
  // Bootstrap hooks (see useAppBootstrap for behavior; each wraps one concern
  // that used to be an inline useEffect in this file).
  useAuthInit();
  useAppServices();
  useOrientationSafeArea();
  useNativeBackButton();
  const { isOffline, retry: handleRetry } = useNetworkStatus();

  // Get celebration state (must be before any conditional returns)
  const isOpen = useCelebrationStore((state) => state.isOpen);
  const type = useCelebrationStore((state) => state.type);
  const data = useCelebrationStore((state) => state.data);
  const closeCelebration = useCelebrationStore((state) => state.closeCelebration);

  return (
    <>
      {/* Show offline indicator when network is unavailable */}
      {isOffline ? <PageLoader isOffline={isOffline} onRetry={handleRetry} /> : null}

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={isOpen}
        onClose={closeCelebration}
        type={type || 'domain'}
        data={data || {}}
      />

      {/* Trial countdown / expired modals */}
      <TrialModals />

      {/* Onboarding walkthrough for new trial users */}
      <OnboardingOverlay />
      <OnboardingNavGuard />

      {/* Signed-out visitors on public pages: the circle-starting notice (§5).
          Renders nothing signed in, on auth pages, for bots or in the prerender. */}
      <PublicStartingSoonHost />

      <LazyRoutes>
        <AppInit />
        <Routes>
          {/* Landing page - web only, redirects authenticated users to /app */}
          <Route path="/" element={<LandingRoute />} />

          {/* Protected routes - require authentication, all under /app */}
          <Route element={<ProtectedRoute />}>
            {/* Guests get the REAL Course + Progress pages — their stores serve the
                anonymous fresh-user data from the public preview endpoints (full tree
                visible, only the intro unlocked). Same views, just limited. */}
            <Route path="/app" element={<Course />} />
            <Route path="/app/progress" element={<Progress />} />
            {/* Backward compatibility redirects */}
            <Route path="/app/paths" element={<Navigate to="/app" replace />} />
            <Route path="/paths" element={<Navigate to="/app" replace />} />
            {/* Real pages for guests. Games' menu + PitfallSwipe are fully client-side
                (guests can play); only Echo Arena is gated, inside games.tsx. Empathy
                renders its natural locked state off the guest's zero progress. */}
            <Route path="/app/games/*" element={<Games />} />
            <Route path="/app/empathy" element={<Empathy />} />
            <Route
              path="/app/empathy/listening-schedule"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Set your listening hours"
                      description="Offer empathy at times that suit you. Create a free account to set your listening hours."
                    />
                  }
                >
                  <ListeningSchedule />
                </GuestGated>
              }
            />
            {/* My bookings (MY-BOOKINGS.md PR A): every booked call, both roles. */}
            <Route
              path="/app/bookings"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="My bookings"
                      description="Book empathy calls with people in your groups. Create a free account to see your bookings."
                    />
                  }
                >
                  <MyBookings />
                </GuestGated>
              }
            />
            {/* Book a call hub (MY-BOOKINGS.md PR B): by time or by person. */}
            <Route
              path="/app/book"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Book a call"
                      description="Book a 1:1 empathy call with a listener. Create a free account to book."
                    />
                  }
                >
                  <BookACall />
                </GuestGated>
              }
            />
            <Route
              path="/app/book/time"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Book a call"
                      description="Book a 1:1 empathy call with a listener. Create a free account to book."
                    />
                  }
                >
                  <BookByTime />
                </GuestGated>
              }
            />
            <Route
              path="/app/book/person"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Book a call"
                      description="Book a 1:1 empathy call with a listener. Create a free account to book."
                    />
                  }
                >
                  <BookByPerson />
                </GuestGated>
              }
            />
            {/* Move a booked call to another free time (PR A item 3). */}
            <Route
              path="/app/bookings/:id/reschedule"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Your booked call"
                      description="Book empathy calls with people in your groups. Create a free account to see your bookings."
                    />
                  }
                >
                  <RescheduleBooking />
                </GuestGated>
              }
            />
            {/* A booked 1:1 call (P8): every booking push and email opens it. */}
            <Route
              path="/app/bookings/:id"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Your booked call"
                      description="Book empathy calls with people in your groups. Create a free account to see your bookings."
                    />
                  }
                >
                  <BookedCall />
                </GuestGated>
              }
            />
            <Route path="/app/ai-buddy" element={<AiBuddySessionPage />} />
            <Route path="/app/circles/:id" element={<CircleSessionPage />} />
            {/* Dev-only Circle Test Room — auth-gated here; backend route
                only exists in local/testing (+staging admin), never prod. */}
            <Route path="/dev/circle-test-room" element={<CircleTestRoom />} />
            {/* The test room's opposite: opens in an exchange so there is a
                camera control to look at. Registered beside it, and equally
                harmless in production — the dev route it needs is not
                registered there, so the page only ever says so. */}
            <Route path="/dev/circle-rehearsal" element={<CircleRehearsal />} />
            {EmpathyTestCall ? (
              <Route path="/dev/empathy-test-call" element={<EmpathyTestCall />} />
            ) : null}
            <Route path="/app/circles/cohorts" element={<Navigate to="/app/empathy" replace />} />
            <Route
              path="/app/circles/cohorts/create"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Create your own circle group"
                      description="Host recurring empathy circles for your community. Create a free account to start a group."
                    />
                  }
                >
                  <CreateCohortPage />
                </GuestGated>
              }
            />
            <Route path="/app/circles/cohorts/:id" element={<CohortDetailPage />} />
            <Route path="/app/circles/cohorts/:id/dashboard" element={<CohortDashboardPage />} />
            <Route path="/app/conversations" element={<Conversations />} />
            <Route
              path="/app/wallet"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Your wallet"
                      description="Send appreciation tips on-chain. Create a free account to set up your wallet."
                    />
                  }
                >
                  <Wallet />
                </GuestGated>
              }
            />
            {/* Tools MENU is real for guests (it's static — breadth sells); each
                individual AI tool is the gated surface. */}
            <Route path="/app/tools" element={<Tools />} />
            <Route
              path="/app/tools/giraffe-translator"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Giraffe Translator"
                      description="Turn judgmental 'jackal' language into empathic 'giraffe' language with AI. Create a free account to use it."
                    />
                  }
                >
                  <GiraffeTranslator />
                </GuestGated>
              }
            />
            <Route
              path="/app/tools/needs-guesser"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Needs Guesser"
                      description="AI help identifying the needs underneath a situation. Create a free account to use it."
                    />
                  }
                >
                  <NeedsGuesser />
                </GuestGated>
              }
            />
            <Route
              path="/app/profile"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Your profile"
                      description="Claim your profile, save your progress, and unlock the full app."
                    />
                  }
                >
                  <Profile />
                </GuestGated>
              }
            />
            <Route path="/app/credits/usage" element={<CreditsUsagePage />} />
            <Route path="/app/referrals" element={<ReferralsPage />} />
            <Route path="/app/languages" element={<LanguagesPage />} />
            <Route path="/app/languages/:code" element={<LanguagesPage />} />
            <Route path="/app/profile/notifications" element={<NotificationPreferencesPage />} />
            <Route path="/app/privacy-dashboard" element={<PrivacyDashboard />} />
            <Route path="/app/lesson/:id" element={<Lesson />} />
            <Route path="/app/lesson/:id/results" element={<LessonResults />} />
            <Route path="/app/quiz/:id" element={<Quiz />} />
            <Route
              path="/app/empy/:id"
              element={
                <GuestGated
                  guest={
                    <GuestWall
                      title="Empy, your AI tutor"
                      description="Personal NVC coaching with streaming AI. Create a free account to chat."
                    />
                  }
                >
                  <AiTutor />
                </GuestGated>
              }
            />
            <Route path="/app/test-path-completion" element={<TestPathCompletion />} />
            <Route path="/app/test-course-completion" element={<TestCourseCompletion />} />
            <Route path="/app/dev/achievement-test" element={<AchievementTest />} />
            <Route path="/app/dev/tipping-test" element={<TippingTest />} />
            {/* Legacy routes redirect to /app versions */}
            <Route path="/progress" element={<Navigate to="/app/progress" replace />} />
            <Route path="/games/*" element={<Navigate to="/app/games" replace />} />
            <Route path="/empathy" element={<Navigate to="/app/empathy" replace />} />
            <Route path="/conversations" element={<Navigate to="/app/conversations" replace />} />
            <Route path="/wallet" element={<Navigate to="/app/wallet" replace />} />
            <Route path="/dashboard/wallet" element={<Navigate to="/app/wallet" replace />} />
            <Route path="/tools" element={<Navigate to="/app/tools" replace />} />
            <Route
              path="/tools/giraffe-translator"
              element={<Navigate to="/app/tools/giraffe-translator" replace />}
            />
            <Route
              path="/tools/needs-guesser"
              element={<Navigate to="/app/tools/needs-guesser" replace />}
            />
            <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
            <Route
              path="/privacy-dashboard"
              element={<Navigate to="/app/privacy-dashboard" replace />}
            />
            <Route path="/lesson/:id" element={<DynamicRedirect to="/app/lesson/:id" />} />
            <Route
              path="/lesson/:id/results"
              element={<DynamicRedirect to="/app/lesson/:id/results" />}
            />
            <Route path="/quiz/:id" element={<DynamicRedirect to="/app/quiz/:id" />} />
            <Route path="/empy/:id" element={<DynamicRedirect to="/app/empy/:id" />} />
            <Route
              path="/test-path-completion"
              element={<Navigate to="/app/test-path-completion" replace />}
            />
            <Route
              path="/test-course-completion"
              element={<Navigate to="/app/test-course-completion" replace />}
            />
            <Route
              path="/dev/achievement-test"
              element={<Navigate to="/app/dev/achievement-test" replace />}
            />
          </Route>

          {/* Public routes - accessible without authentication */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Google OAuth callback - accessible without authentication */}
          <Route path="/auth/google/callback" element={<GoogleCallback />} />

          {/* Beta waitlist landing (backend redirects waitlisted signups here) */}
          <Route path="/waitlist" element={<Waitlist />} />

          {/* Quiz assessment - public, no auth required */}
          <Route path="/quiz-assessment" element={<QuizAssessment />} />
          <Route path="/quiz-assessment/result/:slug" element={<ShareResults />} />

          {/* Blog / learn pages - public */}
          <Route path="/learn" element={<LearnIndex />} />
          <Route path="/learn/:slug" element={<LearnArticle />} />

          {/* Reddit deep link: question uid → quiz (handles auth internally) */}
          <Route path="/quiz-ref/:uid" element={<QuizRefPage />} />

          {/* Circle invite deep link - renders directly (handles auth internally) */}
          <Route path="/circles/join/:inviteCode" element={<JoinInvitePage />} />

          {/* Public cohort page - accessible to everyone */}
          <Route path="/circles/:id" element={<PublicCohortPage />} />

          {/* Dev preview pages (local only). The audio and device test pages
              further down stay registered in every build on purpose. */}
          {import.meta.env.DEV && TurnDisplayPreview ? (
            <Route path="/dev/turn-display" element={<TurnDisplayPreview />} />
          ) : null}
          {import.meta.env.DEV && CirclePreview ? (
            <Route path="/dev/circle-preview" element={<CirclePreview />} />
          ) : null}
          {/* Dev-only, and gated rather than merely named that way: this one is
              a design harness with fake people in it, and the surrounding
              routes are deliberately left alone — a Capacitor build is a
              PRODUCTION build, and the audio/circle test pages below are how
              on-device debugging is done. */}
          {import.meta.env.DEV && AmbientViewPreview ? (
            <Route path="/dev/ambient-view" element={<AmbientViewPreview />} />
          ) : null}
          {import.meta.env.DEV && SessionMenuPreview ? (
            <Route path="/dev/session-menu" element={<SessionMenuPreview />} />
          ) : null}
          {import.meta.env.DEV && CameraCoachPreview ? (
            <Route path="/dev/camera-coach" element={<CameraCoachPreview />} />
          ) : null}
          {import.meta.env.DEV && DesignSystemGallery ? (
            <Route path="/dev/design-system" element={<DesignSystemGallery />} />
          ) : null}
          {import.meta.env.DEV && ToolsPreview ? (
            <Route path="/dev/tools-preview" element={<ToolsPreview />} />
          ) : null}
          {import.meta.env.DEV && ToastsPreview ? (
            <Route path="/dev/toasts" element={<ToastsPreview />} />
          ) : null}
          <Route path="/dev/reaction-test" element={<ReactionTest />} />
          <Route path="/dev/audio-test" element={<AudioTest />} />
          {/* Judge the circle's music by ear, on the device that plays it.
              Ungated like its siblings above: a local-mobile build has
              import.meta.env.DEV === false, and the phone is the whole point.
              The only link in is the VITE_SUBSCRIPTION_DEBUG-gated menu. */}
          <Route path="/dev/sound-panel" element={<SoundPanel />} />
          <Route path="/dev/janus-audio-test" element={<JanusAudioTest />} />
          {import.meta.env.DEV && SuperReactionsGallery ? (
            <Route path="/dev/super-reactions-gallery" element={<SuperReactionsGallery />} />
          ) : null}
          {import.meta.env.DEV && RedditDiscussPreview ? (
            <Route path="/dev/reddit-discuss-preview" element={<RedditDiscussPreview />} />
          ) : null}
          {import.meta.env.DEV && CohortSettingsPreview ? (
            <Route path="/dev/cohort-settings-preview" element={<CohortSettingsPreview />} />
          ) : null}
          {import.meta.env.DEV && OrganizerToolsPreview ? (
            <Route path="/dev/organizer-tools" element={<OrganizerToolsPreview />} />
          ) : null}
          {import.meta.env.DEV && HistoryCardPreview ? (
            <Route path="/dev/history-card" element={<HistoryCardPreview />} />
          ) : null}
          {import.meta.env.DEV && FavoriteExplainerGallery ? (
            <Route path="/dev/favorite-explainer-gallery" element={<FavoriteExplainerGallery />} />
          ) : null}

          {/* Conflict resolution service page - public */}
          <Route path="/conflict-resolution" element={<ConflictResolution />} />

          {/* Legal pages - accessible without authentication */}
          <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />

          {/* Subscription pages - accessible after Stripe checkout */}
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

          {/* Email verification - accessible with or without authentication */}
          {/* Where the emailed link lands after the server verified: renders from ?status= */}
          <Route path="/verify-email/result" element={<EmailVerifyResult />} />
          {/* The app's deep link and links sent before the server-first change */}
          <Route path="/verify-email/:id/:hash" element={<EmailVerify />} />

          {/* The session-note mail's one-tap RSVP (EMAIL-APP-LINKS §2): outside
              ProtectedRoute, because the signed link is the authorisation and a
              signed-out member must still be able to answer. */}
          <Route path="/app/rsvp/:circle/:user/:status" element={<RsvpLinkPage />} />

          {/* Public profile - accessible to everyone */}
          <Route path="/u/:username" element={<PublicProfile />} />
          {/* Language share landing (LANGUAGE-REQUESTS.md §9): public, noindex, client-rendered. */}
          <Route path="/languages/:code" element={<PublicLanguagePage />} />

          {/* Fallback route for unmatched paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </LazyRoutes>

      {/* Debug component for development */}
      {/* <DebugInfo /> */}
    </>
  );
}

export default AppRouter;
